import {
    autocompleteCanonicalAddress,
    autocompleteFullAddress,
    clearUnresolvedAutocompleteLatLngPatch,
    isResolvedAutocompleteAddress,
} from '@/utilities/address';

const compactObject = (obj) => Object.fromEntries(Object.entries(obj).filter(([, value]) => value !== undefined));

/**
 * Whether a geocoding leg status means the address needs a pin review.
 *
 * @param {string} [status] The leg's `geocoding[leg].status`.
 * @returns {boolean} True for `PARTIAL`.
 */
export const isReviewGeocodingStatus = (status) => status === 'PARTIAL';

/**
 * Copy of the order's `geocoding` map without one leg's entry.
 *
 * @param {Object|null|undefined} geocoding The order's `geocoding` map.
 * @param {string} leg Leg key to remove.
 * @returns {Object} A new `geocoding` map.
 */
export const clearGeocodingLeg = (geocoding, leg) => {
    const next = { ...(geocoding || {}) };
    delete next[leg];
    return next;
};

/**
 * One-line label for a geocoding suggestion, for the suggested-pin prompt.
 *
 * @param {Object} [suggestion] A leg's `geocoding[leg].suggestion`.
 * @returns {string} The full address, or its parts joined with commas.
 */
export const formatGeocodingSuggestion = (suggestion = {}) => {
    return (
        suggestion.full_address ||
        suggestion.address ||
        [suggestion.street, suggestion.city, suggestion.state, suggestion.zip].filter(Boolean).join(', ')
    );
};

/**
 * Map coordinates from a geocoding suggestion.
 *
 * @param {Object} [suggestion] A leg's `geocoding[leg].suggestion`.
 * @returns {{lat: number, lng: number}|null} The coordinates, or null when either is missing.
 */
export const geocodingSuggestionCoords = (suggestion = {}) => {
    return suggestion?.lat != null && suggestion?.lng != null ? { lat: suggestion.lat, lng: suggestion.lng } : null;
};

/**
 * Whether a geocoding suggestion is only an approximate location, so the UI can label its pin that way.
 *
 * @param {Object} [suggestion] A leg's `geocoding[leg].suggestion`.
 * @returns {boolean}
 */
export const isApproximateGeocodingSuggestion = (suggestion = {}) => {
    return ['APPROXIMATE', 'GEOMETRIC_CENTER'].includes(suggestion.location_type);
};

/**
 * Whether a leg is awaiting review and has a suggested pin the user can apply.
 *
 * @param {Object|null|undefined} geocoding The order's `geocoding` map.
 * @param {string} leg Leg key, from `genAttributes(order).geocoding`.
 * @returns {boolean}
 */
export const hasReviewGeocodingSuggestion = (geocoding, leg) => {
    const suggestion = geocoding?.[leg]?.suggestion;
    return !!(isReviewGeocodingStatus(geocoding?.[leg]?.status) && suggestion?.lat != null && suggestion?.lng != null);
};

/**
 * Next `geocoding` map after an address autocomplete pick. A partial match with
 * coordinates becomes a `PARTIAL` review suggestion on the leg; otherwise the leg is cleared.
 *
 * @param {Object|null|undefined} geocoding The order's `geocoding` map.
 * @param {string} leg Leg key to update.
 * @param {string} value The text the user entered.
 * @param {Object} split The parsed autocomplete result.
 * @returns {Object} A new `geocoding` map.
 */
export const geocodingFromAutocomplete = (geocoding, leg, value, split) => {
    const next = clearGeocodingLeg(geocoding, leg);
    if (isResolvedAutocompleteAddress(split) || split?.lat == null || split?.lng == null) {
        return next;
    }

    next[leg] = {
        status: 'PARTIAL',
        source: 'autocomplete',
        suggestion: compactObject({
            address: split.address,
            full_address: autocompleteFullAddress(value, split),
            street: split.street || split.addressLine,
            city: split.city,
            state: split.state,
            zip: split.zip,
            lat: split.lat,
            lng: split.lng,
            location_type: split.locationType || 'GEOMETRIC_CENTER',
        }),
        diagnostics: {
            reason_codes: ['AUTOCOMPLETE_PARTIAL'],
        },
    };
    return next;
};

/**
 * `modifyOrder` patch for an address autocomplete pick: the address fields, cleared
 * failure flags and unresolved coordinates, and the updated `geocoding` map.
 *
 * @param {Object} args
 * @param {Object} args.attrs Order column names for this address, from `genAttributes`.
 * @param {Object|null|undefined} args.geocoding The order's `geocoding` map.
 * @param {string} args.leg Geocoding leg key for this address.
 * @param {string} args.value The text the user entered.
 * @param {Object} args.split The parsed autocomplete result.
 * @returns {Object} The order patch.
 */
export const autocompleteAddressPatch = ({ attrs, geocoding, leg, value, split }) => ({
    [attrs.geocode_failed]: false,
    [attrs.address]: autocompleteCanonicalAddress(value, split),
    [attrs.street]: split.street,
    [attrs.city]: split.city,
    [attrs.state]: split.state,
    [attrs.zip]: split.zip,
    ...clearUnresolvedAutocompleteLatLngPatch(split, attrs.lat, attrs.long),
    auto_assign_failed: false,
    geocoding: geocodingFromAutocomplete(geocoding, leg, value, split),
});
